import apiClient from '../utils/api';

export interface ApiProduct {
  productId: string;
  vendorId: string;
  sectionId: string;
  orderInSection: string;
  title: string;
  shortDescription?: string;
  fullDescription: string;
  imageURLs: string[];
  minPrice: number;
  amount: number;
  status: string; // "Active", "Draft", "Suspended", etc.
  createdAt: string;
  updatedAt: string;
  locale?: {
    en?: {
      title?: string;
      shortDescription?: string;
      fullDescription?: string;
    };
    ar?: {
      title?: string;
      shortDescription?: string;
      fullDescription?: string;
    };
  };
  requirements?: Array<{
    requirementId: string;
    productId: string;
    name: string;
    required?: boolean;
    amount: string;
    unit?: string;
    createdAt?: string;
    updatedAt?: string;
  }>;
  packages?: Array<{
    packageId: string;
    productId: string;
    title: string;
    subTitle?: string;
    type: 'one-select' | 'multi-select' | 'addons';
    required?: boolean;
    options?: Array<{
      id: string;
      label: string;
      price?: number;
      locale?: {
        en?: { label?: string };
        ar?: { label?: string };
      };
    }>;
    items?: Array<{
      id: string;
      label: string;
      pricePerUnit: number;
      minUnits: number;
      maxUnits: number;
      locale?: {
        en?: { label?: string };
        ar?: { label?: string };
      };
    }>;
    maxSelections?: number;
    maxCustomizableItems?: number;
    locale?: {
      en?: {
        title?: string;
        subTitle?: string;
      };
      ar?: {
        title?: string;
        subTitle?: string;
      };
    };
    // Legacy fields for backward compatibility
    name?: string;
    price?: number;
    minOrder?: number;
    maxOrder?: number;
    description?: string;
    createdAt: string;
    updatedAt: string;
  }>;
}

export interface ProductsApiResponse {
  success: boolean;
  message?: string;
  products: ApiProduct[];
}

export interface SectionLocale {
  en?: { name?: string };
  ar?: { name?: string };
}

export interface ProductSection {
  sectionId: string;
  name: string;
  displayOrder: number;
  products: ApiProduct[];
  locale?: SectionLocale;
}

export interface ProductsGroupedBySectionsApiResponse {
  success: boolean;
  message?: string;
  sections: ProductSection[];
}

// Get all products for a vendor (returns products grouped by sections)
export const getProducts = async (vendorId: string, status?: string, searchQuery?: string): Promise<ProductsGroupedBySectionsApiResponse> => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (searchQuery) params.append('q', searchQuery);
  
  const queryString = params.toString();
  const url = `/vendors/${vendorId}/products${queryString ? `?${queryString}` : ''}`;
  
  const response = await apiClient.get<{ message?: string; data: ProductSection[] }>(url);
  return {
    success: true,
    message: response.data.message,
    sections: response.data.data ?? [],
  };
};

// Get a single product by ID (vendorId comes from auth context)
export const getProduct = async (productId: string): Promise<ApiProduct> => {
  const response = await apiClient.get<{ success: boolean; message?: string; data: { product: ApiProduct } }>(
    `/vendors/products/${productId}`
  );
  return response.data.data?.product ?? response.data.data as any;
};

// Get products by category (you can implement this when the API endpoint supports it)
// Note: Currently products don't have a category field, this can be implemented when categories are added
export const getProductsByCategory = async (vendorId: string, category: string): Promise<ProductsGroupedBySectionsApiResponse> => {
  const response = await getProducts(vendorId);
  // TODO: Implement category filtering when category field is available in API
  return {
    ...response,
    sections: [], // Return empty for now until category support is added
  };
};

// Get featured products (you can implement this when the API endpoint supports it)
// Note: Currently products don't have an isFeatured field, this can be implemented when featured support is added
export const getFeaturedProducts = async (vendorId: string): Promise<ProductsGroupedBySectionsApiResponse> => {
  const response = await getProducts(vendorId);
  // TODO: Implement featured filtering when isFeatured field is available in API
  return {
    ...response,
    sections: [], // Return empty for now until featured support is added
  };
};

// Type for creating/updating products (allows CreateRequirementRequest and CreatePackageRequest)
export type CreateOrUpdateProductRequest = Omit<Partial<ApiProduct>, 'requirements' | 'packages'> & {
  requirements?: CreateRequirementRequest[];
  packages?: CreatePackageRequest[];
};

// Create a new product (requires vendorId)
export const createProduct = async (vendorId: string, productData: CreateOrUpdateProductRequest): Promise<ApiProduct> => {
  const response = await apiClient.post<{ message?: string; data: { productId: string } }>(
    `/vendors/${vendorId}/products`,
    productData
  );
  return response.data.data as any;
};

// Update a product
export const updateProduct = async (productId: string, productData: CreateOrUpdateProductRequest): Promise<ApiProduct> => {
  const response = await apiClient.patch<ApiProduct>(`/vendors/products/${productId}`, productData);
  return response.data;
};

// Delete a product
export const deleteProduct = async (productId: string): Promise<any> => {
  const response = await apiClient.delete(`/vendors/products/${productId}`);
  return response.data;
};

export interface ReorderProductItem {
  productId: string;
  sectionId: string;
  displayOrder: number;
}

/** Persist within-section product order (displayOrder). Does not move products across sections. */
export const reorderProducts = async (
  vendorId: string,
  products: ReorderProductItem[],
): Promise<void> => {
  await apiClient.put(`/vendors/products/order?vendorId=${encodeURIComponent(vendorId)}`, {
    products,
  });
};

// Packages API
export interface ApiPackage {
  packageId: string;
  productId: string;
  title: string;
  subTitle?: string;
  type: 'one-select' | 'multi-select' | 'addons';
  required?: boolean;
  displayOrder: number;
  options?: Array<{
    id: string;
    label: string;
    price?: number;
  }>;
  items?: Array<{
    id: string;
    label: string;
    pricePerUnit: number;
    minUnits: number;
    maxUnits: number;
  }>;
  maxSelections?: number;
  maxCustomizableItems?: number;
  // Legacy fields for backward compatibility
  name?: string;
  price?: number;
  minOrder?: number;
  maxOrder?: number;
  description?: string;
}

export interface CreatePackageRequest {
  title: string;
  subTitle?: string;
  type: 'one-select' | 'multi-select' | 'addons';
  required?: boolean;
  displayOrder: number;
  options?: Array<{
    id: string;
    label: string;
    price?: number;
    locale?: {
      en?: { label?: string };
      ar?: { label?: string };
    };
  }>;
  items?: Array<{
    id: string;
    label: string;
    pricePerUnit: number;
    minUnits: number;
    maxUnits: number;
    locale?: {
      en?: { label?: string };
      ar?: { label?: string };
    };
  }>;
  maxSelections?: number;
  maxCustomizableItems?: number;
  locale?: {
    en?: {
      title?: string;
      subTitle?: string;
    };
    ar?: {
      title?: string;
      subTitle?: string;
    };
  };
  // Legacy fields for backward compatibility
  name?: string;
  price?: number;
  minOrder?: number;
  maxOrder?: number;
  description?: string;
}

export const createPackage = async (productId: string, packageData: CreatePackageRequest): Promise<ApiPackage> => {
  const response = await apiClient.post<{ message?: string; data: { package: ApiPackage } }>(
    `/vendors/products/${productId}/packages`,
    packageData
  );
  return response.data.data?.package ?? response.data as any;
};

// Requirements API
export type RequirementFieldType = 'text' | 'checkbox' | 'double-text';

export interface ApiRequirement {
  requirementId: string;
  productId: string;
  name: string;
  required?: boolean;
  amount: string;
  unit?: string;
}

export interface CreateRequirementRequest {
  name: string;
  required?: boolean;
  amount: string | number;
  unit?: string;
}

export const createRequirement = async (productId: string, requirementData: CreateRequirementRequest): Promise<ApiRequirement> => {
  const response = await apiClient.post<{ message?: string; data: { requirement: ApiRequirement } }>(
    `/vendors/products/${productId}/requirements`,
    requirementData
  );
  return response.data.data?.requirement ?? response.data as any;
};

export const updateRequirement = async (productId: string, requirementId: string, requirementData: Partial<CreateRequirementRequest>): Promise<ApiRequirement> => {
  const response = await apiClient.patch<{ message?: string; data: { requirement: ApiRequirement } }>(
    `/vendors/products/${productId}/requirements/${requirementId}`,
    requirementData
  );
  return response.data.data?.requirement ?? response.data as any;
};

export const deleteRequirement = async (productId: string, requirementId: string): Promise<void> => {
  await apiClient.delete(`/vendors/products/${productId}/requirements/${requirementId}`);
};


// Requirement templates - backend is source of truth

export interface ApiRequirementTemplate {
  title: string;
  fieldType: RequirementFieldType;
  iconURL?: string;
  /** Default unit for text requirements (e.g. "QAR", "Hours"), stored in backend. */
  defaultUnit?: string;
}

export const getRequirementTemplates = async (): Promise<ApiRequirementTemplate[]> => {
  const response = await apiClient.get<{
    success: boolean;
    message?: string;
    data: { requirements: ApiRequirementTemplate[] };
  }>('/vendors/requirements');

  return response.data.data?.requirements ?? [];
};

export const updatePackage = async (productId: string, packageId: string, packageData: Partial<CreatePackageRequest>): Promise<ApiPackage> => {
  const response = await apiClient.patch<{ message?: string; data: { package: ApiPackage } }>(
    `/vendors/products/${productId}/packages/${packageId}`,
    packageData
  );
  return response.data.data?.package ?? response.data as any;
};

export const deletePackage = async (productId: string, packageId: string): Promise<void> => {
  await apiClient.delete(`/vendors/products/${productId}/packages/${packageId}`);
};

